import {
  createTheme,
  defaultVariantColorsResolver,
  type CSSVariablesResolver,
  type MantineColorsTuple,
  type VariantColorsResolver,
} from '@mantine/core';

/**
 * Tema do painel.
 *
 * Os VALORES deste arquivo vêm de dois projetos open source, os dois MIT, e
 * nada foi copiado deles além de números e cores:
 *
 *  - shadcndashboard (https://github.com/shadcndashboard/shadcndashboard):
 *    paleta, cor de ação, raios, espaçamento e tipografia. Guarda os tokens num
 *    bloco `@theme` do Tailwind v4, então precisou de tradução.
 *  - mantine-analytics-dashboard
 *    (https://github.com/design-sparx/mantine-analytics-dashboard): as
 *    SOMBRAS. Mesma biblioteca que a nossa, então entraram direto.
 *
 * Do segundo veio só isso, de propósito: o `createTheme` dele não tem paleta
 * (usa o `blue` de catálogo), não tem `Component.extend()` nem `classNames`, e
 * os `styles` que ele define fixam altura e corpo de letra em pixel — o que
 * atropelaria a prop `size`, usada centenas de vezes aqui. Ver o README.
 *
 * A tradução é toda aqui. Nenhum componente ganhou CSS por causa dela — a
 * única exceção é o fundo da PÁGINA, explicada em `variaveisDeTema`.
 */

/**
 * A cor de AÇÃO é o marinho do Aeroclube.
 *
 * `#0A1A2F` é a cor institucional declarada pelo site. Entrou no lugar do
 * quase-preto neutro que vinha do shadcndashboard: em contraste os dois são
 * equivalentes (17,48:1 contra 17,93:1 sobre branco, ambos muito acima de
 * AAA), mas o marinho carrega a marca e o preto não carregava nada. A
 * diferença entre eles é de MATIZ, não de claridade — e matiz o olho distingue
 * mesmo com luminância igual.
 *
 * Não é o marinho da logo (`#070838`): aquele puxa para o violeta e encostaria
 * no selo de veredito.
 *
 * A RAMPA foi construída em OKLCH com matiz fixo em 256°, que é o do
 * `#0A1A2F`, e com a mesma curva de claridade da rampa cinza anterior — a
 * troca não muda o peso percebido de nada, só a temperatura.
 *
 * A CROMA é de propósito quase zero na ponta clara e cheia no fundo:
 *
 *  - do 5 ao 9 o marinho aparece inteiro, e é ali que a ação mora (o 6 é o
 *    botão cheio, o 7 é o hover dele);
 *  - do 0 ao 2 ela quase some, e isso resolve uma colisão real. O 1 é o fundo
 *    do `variant="light"`, que é o padrão dos nossos selos. Com a croma
 *    distribuída por igual, esse fundo saía `#dee6f1` — a três pontos de
 *    `azul[1]` (`#dbe6f2`), o do selo `raiz` da árvore do menu, que aparece na
 *    MESMA tela que selos sem cor. Dois selos indistinguíveis, um deles
 *    carregando significado. Com a croma rebaixada o fundo é `#e4e5e7`, a um
 *    ponto do cinza de antes: os selos neutros continuam neutros e o `raiz`
 *    continua sendo o único azul.
 */
const tinta: MantineColorsTuple = [
  '#f4f5f6',
  '#e4e5e7', // 1 — fundo do `variant="light"`; neutro de propósito
  '#d2d4d8',
  '#9da4ad',
  '#687485',
  '#314157',
  '#0a1a2f', // 6 — a AÇÃO no tema claro: o marinho institucional
  '#051225', // 7 — o hover dele
  '#030d1e',
  '#020a19', // 9 — texto do `variant="light"` no tema claro
];

/**
 * Azul, preservado.
 *
 * Deixou de ser a primária, mas continua na paleta porque ainda carrega
 * significado em um lugar: o selo `raiz` da árvore do menu, que é
 * `color="azul"`. Tirar a tupla quebraria aquele selo sem ganho nenhum.
 */
const azul: MantineColorsTuple = [
  '#eef4fb',
  '#dbe6f2',
  '#b4cbe6',
  '#8aafda',
  '#6897d0',
  '#5287ca',
  '#447ec8',
  '#366cb1',
  '#2c5f9f',
  '#1d518c',
];

/**
 * Cinzas do tema CLARO.
 *
 * O Mantine deriva daqui, e não de variáveis próprias, quase tudo que desenha
 * superfície: `gray-3` vira a borda de `Paper` e do `AppShell`, `gray-4` vira
 * a borda dos campos e do botão padrão, `gray-6` vira o texto apagado. Os
 * valores são os do template (`--border: #e5e5e5`,
 * `--muted-foreground: #737373`).
 */
const cinza: MantineColorsTuple = [
  '#fafafa',
  '#f5f5f5',
  '#ededed',
  '#e5e5e5', // 3 — borda de cartão (template `--border`)
  '#d4d4d4', // 4 — borda de campo e de botão padrão
  '#a3a3a3',
  '#737373', // 6 — texto apagado (template `--muted-foreground`)
  '#525252',
  '#404040',
  '#262626',
];

/**
 * Cinzas do tema ESCURO.
 *
 * Mesma derivação, índices diferentes: `dark-0` é o texto, `dark-2` o texto
 * apagado, `dark-4` as bordas, `dark-6` a superfície de campo e `dark-7` o
 * fundo de CARTÃO. O índice 7 é o ponto importante — ele é
 * `--mantine-color-body`, que o `Paper`, a lateral e o cabeçalho usam como
 * fundo. No template o cartão escuro é `#171717` e a PÁGINA é `#0a0a0a`, dois
 * tons distintos; é isso que dá o destaque do cartão sobre o fundo.
 */
const cinzaEscuro: MantineColorsTuple = [
  '#fafafa', // 0 — texto
  '#e5e5e5',
  '#a1a1a1', // 2 — texto apagado
  '#737373', // 3 — texto de exemplo em campo vazio
  '#2e2e2e', // 4 — bordas (o `rgba(255,255,255,.1)` do template sobre o cartão)
  '#262626', // 5 — estado de passagem do mouse
  '#1f1f1f', // 6 — superfície de campo e de botão padrão
  '#171717', // 7 — fundo de CARTÃO (template `--card`)
  '#111111',
  '#0a0a0a', // 9 — fundo da PÁGINA (template `--background`)
];

/**
 * O texto de botão cheio, quando a cor é a primária.
 *
 * O Mantine resolve `variant="filled"` com texto branco fixo, o que serve ao
 * tema claro (branco sobre `#171717`) e falha no escuro: lá o template INVERTE
 * a ação — ela vira quase-branca (`oklch(0.922 0 0)` = `#e5e5e5`), e branco
 * sobre branco desaparece. Um `primaryShade` não expressa essa inversão,
 * porque o índice escolhido vale para TODAS as cores e arrastaria vermelho,
 * laranja e teal com ele.
 *
 * Então só a primária passa a ler o contraste de uma variável, que
 * `variaveisDeTema` define por esquema. Todas as outras cores saem daqui
 * exatamente como entraram — é o que mantém o vermelho do transbordo, os
 * selos de veredito e os indicadores de conversa idênticos ao que já eram.
 */
const corDeVariante: VariantColorsResolver = (entrada) => {
  const resolvido = defaultVariantColorsResolver(entrada);
  const ehAcao = entrada.color === undefined || entrada.color === 'tinta';
  if (ehAcao && entrada.variant === 'filled') {
    return { ...resolvido, color: 'var(--mantine-primary-color-contrast)' };
  }
  return resolvido;
};

export const tema = createTheme({
  primaryColor: 'tinta',
  colors: { tinta, azul, gray: cinza, dark: cinzaEscuro },
  /**
   * 6 no claro e 4 no escuro — os MESMOS de antes, de propósito.
   *
   * O índice vale para toda cor nomeada, não só para a primária: mudá-lo
   * mexeria no vermelho de `variant="filled"` e em todo selo de status. A
   * inversão que o tema escuro precisa é só da ação, e está em
   * `variaveisDeTema`.
   */
  primaryShade: { light: 6, dark: 4 },
  variantColorResolver: corDeVariante,
  /**
   * O template usa Geist, que vem como `.woff2` no repositório dele — binário,
   * não token. Ficou a pilha do sistema: baixar fonte de fora acrescentaria
   * requisição de rede a um painel que hoje não faz nenhuma.
   */
  fontFamily:
    'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  fontFamilyMonospace: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  black: '#0a0a0a',
  white: '#ffffff',
  /** `--radius: 8px` do template, e a escala que ele deriva daí. */
  radius: { xs: '4px', sm: '6px', md: '8px', lg: '12px', xl: '16px' },
  defaultRadius: 'md',
  /**
   * Sombras do mantine-analytics-dashboard, em cada degrau que o Mantine
   * realmente consome.
   *
   * Lá elas vivem em `other.customShadows`, nomeadas pelo USO — cartão,
   * menu suspenso, modal — e não por tamanho. Traduzir é encaixar cada uma no
   * degrau que o Mantine lê para aquele uso: `sm` é o que os cartões de login
   * e de convite pedem (`shadow="sm"`), `md` é o de menu e popover, e `xl` é
   * o que o Modal usa por padrão (`--mb-shadow` cai em `--mantine-shadow-xl`)
   * — ou seja, todo diálogo do painel.
   *
   * Só `lg` é DERIVADO, interpolando os dois vizinhos: o template não tem
   * degrau nessa posição. As outras quatro são valores dele.
   *
   * Trocaram as da rodada anterior porque aquelas vinham de um tema de
   * Tailwind e não casavam entre si: a `md` era mais difusa e mais clara que a
   * `sm`, que ainda puxava para o azul-acinzentado. Estas são uma rampa só,
   * preta, crescendo em desfoque e em opacidade junto.
   */
  shadows: {
    xs: '0 1px 3px rgba(0, 0, 0, 0.12)',
    sm: '0 2px 8px rgba(0, 0, 0, 0.15)',
    md: '0 4px 16px rgba(0, 0, 0, 0.15)',
    lg: '0 6px 24px rgba(0, 0, 0, 0.18)',
    xl: '0 8px 32px rgba(0, 0, 0, 0.2)',
  },
  /** Malha de 4px, que é a do Tailwind v4 e a que o template usa (`p-2`…`p-8`). */
  spacing: { xs: '8px', sm: '12px', md: '16px', lg: '24px', xl: '32px' },
  /** A escala do Tailwind v4 — que por sorte é a mesma do Mantine. */
  fontSizes: { xs: '12px', sm: '14px', md: '16px', lg: '18px', xl: '20px' },
  /** As alturas de linha que o Tailwind casa com cada tamanho acima. */
  lineHeights: { xs: '1.3333', sm: '1.4286', md: '1.5', lg: '1.5556', xl: '1.5' },
  headings: { fontWeight: '600' },
  components: {
    /**
     * Densidade da tabela, PINADA em pixel.
     *
     * Era `verticalSpacing="xs"`, e `xs` acabou de sair de 10px para 8px com a
     * malha de 4px. Os 10px são decisão anterior — painel lido em tela de
     * balcão, várias linhas por vez — e continuam valendo; o que mudou foi a
     * escala embaixo dela, não a escolha. Medindo o template, a linha dele é
     * ainda mais apertada que esta (ver nota no README).
     */
    Table: { defaultProps: { striped: 'even', highlightOnHover: true, verticalSpacing: 10 } },
    Button: { defaultProps: { variant: 'default' } },
    Badge: { defaultProps: { variant: 'light' } },
    /**
     * Item ativo em tinta sólida, com o mesmo raio do resto do painel.
     *
     * `variant="filled"` em vez do `light` padrão do Mantine. As variáveis
     * `--nl-bg`/`--nl-color` que isso define só valem no item ATIVO (o CSS do
     * Mantine as aplica sob `[data-active]`), então o resto do menu continua
     * sem fundo e com o texto do tema, como no template.
     *
     * O RAIO precisa ser dito aqui porque o `NavLink` não tem nenhum: o CSS do
     * Mantine não declara `border-radius` para ele e não expõe um `--nl-radius`
     * para sobrescrever. Sem isto, o bloco preto do item ativo saía de canto
     * reto ao lado de cartões e blocos de ajuda arredondados.
     *
     * O valor é a VARIÁVEL do tema, não um número: `--mantine-radius-default` é
     * o que `--paper-radius` e `--accordion-radius` já leem, então os três
     * acompanham `defaultRadius` juntos se ele mudar um dia.
     */
    NavLink: {
      /**
       * `noWrap` e a GARANTIA de que nenhuma descricao quebra em duas linhas.
       *
       * O CSS do Mantine ja traz `overflow: hidden` e `text-overflow: ellipsis`
       * no rotulo e na descricao; o que faltava era o `white-space: nowrap`,
       * que so entra com esta prop. Medidas as oito descricoes, nenhuma chega a
       * truncar depois que os vaos do item ficaram mais justos (ver
       * `estilo.css`) — isto existe para o dia em que uma delas crescer ou a
       * fonte do sistema render mais largo. Reticencias desalinham menos que
       * uma segunda linha.
       */
      defaultProps: { fw: 500, variant: 'filled', noWrap: true },
      styles: { root: { borderRadius: 'var(--mantine-radius-default)' } },
    },
    Divider: { defaultProps: { color: 'var(--mantine-color-default-border)' } },
  },
});

/**
 * O que o Mantine não deriva das tuplas.
 *
 * Três coisas:
 *
 *  1. O fundo da PÁGINA. O Mantine usa uma variável só —
 *     `--mantine-color-body` — para o `body` e para o `Paper`, a lateral e o
 *     cabeçalho. O template separa as duas: página `#fafafa`/`#0a0a0a`, cartão
 *     `#ffffff`/`#171717`. Mantida a variável do Mantine como SUPERFÍCIE, o
 *     fundo da página vira token próprio — é o que faz a lateral e os cartões
 *     se destacarem em vez de se fundirem com a tela.
 *  2. O contraste da ação, lido por `corDeVariante`.
 *  3. A inversão da ação no escuro, que o `primaryShade` não alcança.
 */
export const variaveisDeTema: CSSVariablesResolver = () => ({
  variables: {},
  light: {
    '--painel-fundo-pagina': '#fafafa',
    /** O texto corrido é NEUTRO: a marca é a ação, não o parágrafo. */
    '--mantine-color-text': '#0a0a0a',
    '--mantine-primary-color-contrast': '#f4f5f6',
  },
  dark: {
    '--painel-fundo-pagina': '#0a0a0a',
    /**
     * A ação INVERTE no escuro, e com ela a marca sai de cena.
     *
     * Marinho sobre a página `#0a0a0a` seria quase invisível, então o botão
     * vira quase-branco com texto escuro — decisão da primeira rodada, mantida
     * de propósito: botão legível vale mais que marca num tema que é
     * preferência de aparelho. O que o marinho ainda faz aqui é tingir o TEXTO
     * do botão (`contrast`), que é o único traço de marca que sobrevive.
     *
     * Os três valores saem da própria rampa, em vez de serem hexadecimais
     * soltos: `tinta[1]` e `tinta[0]` no fundo, `tinta[6]` no texto.
     */
    '--mantine-color-tinta-filled': '#e4e5e7',
    '--mantine-color-tinta-filled-hover': '#f4f5f6',
    '--mantine-primary-color-contrast': '#0a1a2f',
    /**
     * O contador cinza cheio.
     *
     * No escuro ele caía no tom 4 da rampa — claro demais para o texto branco
     * que o `filled` impõe, e o número é justamente o sinal. Um tom médio o
     * devolve à legibilidade sem mudar o que ele significa.
     */
    '--mantine-color-gray-filled': '#525252',
    '--mantine-color-gray-filled-hover': '#404040',
  },
});

/**
 * Chave do localStorage com o tema escolhido.
 *
 * Preferência de APARELHO, não de usuário: a mesma pessoa pode querer claro
 * no balcão e escuro em casa, e guardar isso no banco faria a escolha viajar
 * junto com o login.
 */
export const CHAVE_TEMA = 'aeroclube-painel-tema';
